import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  type FormAnswerSpacing,
  type FormBannerDisplay,
  type FormBrandMarkPosition,
  type FormBrandMarkStyle,
  type FormBrandSettings,
  type FormLogoPosition,
  type FormLogoShape,
  type FormNavigationLayout,
  type FormProgressPosition,
  type FormQuestionAlignment,
  type FormVerticalAlign,
  getFormBodyFont,
  getFormBrandSettings,
  getFormHeadingFont,
  getFormLayoutSettings,
} from '@/lib/form-theme'
import { cn } from '@/lib/utils'
import { type AppearancePreviewMode, useFormBuilderStore } from '@/stores/formBuilderStore'
import type { FormTheme } from '@/types/form'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowUpToLine,
  Columns2,
  Eye,
  ImageIcon,
  Link,
  ListCollapse,
  MoveVertical,
  PanelBottom,
  PanelTop,
  Pencil,
  Rows2,
  Rows3,
  Rows4,
  RotateCcw,
  SquareSplitHorizontal,
  StretchHorizontal,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { type EditableImageValue, ImageEditorDialog } from './ImageEditorDialog'

type BackgroundType = 'solid' | 'gradient' | 'image' | 'video'
type FontScale = NonNullable<FormTheme['fontScale']>
type SettingsPatch = Partial<Record<string, unknown>>

type FontOption = {
  family: string
  category: string
  description: string
  sample: string
}

type FontPair = {
  name: string
  heading: string
  body: string
  description: string
}

type LayoutPreset = {
  name: string
  description: string
  icon: LucideIcon
  settings: {
    questionAlign: FormQuestionAlignment
    verticalAlign: FormVerticalAlign
    answerSpacing: FormAnswerSpacing
    navigationLayout: FormNavigationLayout
    progressPosition: FormProgressPosition
    showQuestionNumber: boolean
    showBackButton: boolean
  }
  theme?: Pick<FormTheme, 'maxWidth' | 'radius'>
}

type BrandPreset = {
  name: string
  description: string
  settings: Partial<FormBrandSettings>
}

type ThemePreset = {
  name: string
  description: string
  theme: FormTheme
  backgroundType: BackgroundType
}

const themePresets: ThemePreset[] = [
  {
    name: 'Minimal White',
    description: '乾淨白底，適合正式問卷',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#FFFFFF',
      textColor: '#0F172A',
      answerColor: '#475569',
      buttonColor: '#6366F1',
      buttonTextColor: '#FFFFFF',
      accentColor: '#6366F1',
      radius: 8,
      maxWidth: 720,
      fontFamily: 'Geist',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Dark Pro',
    description: '深色專業，適合科技產品',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#0F172A',
      textColor: '#F8FAFC',
      answerColor: '#CBD5E1',
      buttonColor: '#8B5CF6',
      buttonTextColor: '#FFFFFF',
      accentColor: '#A78BFA',
      radius: 12,
      maxWidth: 760,
      fontFamily: 'Inter',
      progressStyle: 'thick',
    },
  },
  {
    name: 'Ocean Blue',
    description: '清爽藍色，適合客戶回饋',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#E0F2FE',
      textColor: '#0C4A6E',
      answerColor: '#075985',
      buttonColor: '#0284C7',
      buttonTextColor: '#FFFFFF',
      accentColor: '#0EA5E9',
      radius: 10,
      maxWidth: 720,
      fontFamily: 'Noto Sans TC',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Forest Green',
    description: '溫和綠色，適合 ESG 與內部調查',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#ECFDF5',
      textColor: '#064E3B',
      answerColor: '#047857',
      buttonColor: '#059669',
      buttonTextColor: '#FFFFFF',
      accentColor: '#10B981',
      radius: 10,
      maxWidth: 720,
      fontFamily: 'Noto Sans TC',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Sunset Orange',
    description: '暖色活力，適合活動報名',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#FFF7ED',
      textColor: '#7C2D12',
      answerColor: '#9A3412',
      buttonColor: '#F97316',
      buttonTextColor: '#FFFFFF',
      accentColor: '#FB923C',
      radius: 14,
      maxWidth: 740,
      fontFamily: 'Geist',
      progressStyle: 'thick',
    },
  },
  {
    name: 'Lavender Dream',
    description: '柔和紫色，適合品牌體驗',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#F5F3FF',
      textColor: '#2E1065',
      answerColor: '#5B21B6',
      buttonColor: '#8B5CF6',
      buttonTextColor: '#FFFFFF',
      accentColor: '#A78BFA',
      radius: 16,
      maxWidth: 760,
      fontFamily: 'Inter',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Rose Gold',
    description: '細緻玫瑰色，適合體驗調查',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#FFF1F2',
      textColor: '#881337',
      answerColor: '#BE123C',
      buttonColor: '#E11D48',
      buttonTextColor: '#FFFFFF',
      accentColor: '#FB7185',
      radius: 12,
      maxWidth: 700,
      fontFamily: 'Noto Sans TC',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Midnight',
    description: '沉穩夜色，適合高端品牌',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#111827',
      textColor: '#F9FAFB',
      answerColor: '#D1D5DB',
      buttonColor: '#7C3AED',
      buttonTextColor: '#FFFFFF',
      accentColor: '#A78BFA',
      radius: 14,
      maxWidth: 760,
      fontFamily: 'Inter',
      progressStyle: 'text',
    },
  },
  {
    name: 'Arctic',
    description: '高對比冷色，適合資料收集',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#F8FAFC',
      textColor: '#0F172A',
      answerColor: '#334155',
      buttonColor: '#0EA5E9',
      buttonTextColor: '#FFFFFF',
      accentColor: '#38BDF8',
      radius: 8,
      maxWidth: 720,
      fontFamily: 'Geist',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Warm Earth',
    description: '溫暖中性色，適合長表單',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#FAF7F2',
      textColor: '#422006',
      answerColor: '#713F12',
      buttonColor: '#A16207',
      buttonTextColor: '#FFFFFF',
      accentColor: '#CA8A04',
      radius: 10,
      maxWidth: 700,
      fontFamily: 'Noto Sans TC',
      progressStyle: 'thin',
    },
  },
  {
    name: 'Neon Cyber',
    description: '高能霓虹，適合創新活動',
    backgroundType: 'gradient',
    theme: {
      backgroundColor: '#09090B',
      textColor: '#ECFEFF',
      answerColor: '#A5F3FC',
      buttonColor: '#22D3EE',
      buttonTextColor: '#0F172A',
      accentColor: '#67E8F9',
      radius: 6,
      maxWidth: 780,
      fontFamily: 'Inter',
      progressStyle: 'thick',
    },
  },
  {
    name: 'Parchment',
    description: '紙本質感，適合內容回饋',
    backgroundType: 'solid',
    theme: {
      backgroundColor: '#FFFBEB',
      textColor: '#451A03',
      answerColor: '#92400E',
      buttonColor: '#B45309',
      buttonTextColor: '#FFFFFF',
      accentColor: '#D97706',
      radius: 4,
      maxWidth: 680,
      fontFamily: 'Noto Serif TC',
      progressStyle: 'text',
    },
  },
]

const fontOptions: FontOption[] = [
  {
    family: 'Geist',
    category: 'Product Sans',
    description: '現代產品介面，字面乾淨、掃描性高',
    sample: '清楚俐落的問卷標題',
  },
  {
    family: 'Inter',
    category: 'UI Sans',
    description: '後台與 SaaS 常用，數字與介面文字穩定',
    sample: 'Efficient form reading',
  },
  {
    family: 'Noto Sans TC',
    category: 'Traditional Chinese',
    description: '繁中內容最穩妥，長問題與說明文字易讀',
    sample: '繁體中文填答體驗',
  },
  {
    family: 'Noto Serif TC',
    category: 'Serif TC',
    description: '文字感較強，適合品牌敘事與教育內容',
    sample: '帶有溫度的內容標題',
  },
  {
    family: 'Roboto',
    category: 'Neutral Sans',
    description: '中性、實用，適合大量資訊',
    sample: 'Reliable survey flow',
  },
  {
    family: 'Open Sans',
    category: 'Readable Sans',
    description: '開放感較高，適合說明文字與選項',
    sample: 'Comfortable answer text',
  },
  {
    family: 'Lato',
    category: 'Friendly Sans',
    description: '柔和友善，適合客服與回饋問卷',
    sample: 'Friendly feedback form',
  },
  {
    family: 'Montserrat',
    category: 'Display Sans',
    description: '標題辨識度強，適合活動與品牌問卷',
    sample: 'Bold campaign title',
  },
  {
    family: 'Source Sans 3',
    category: 'Content Sans',
    description: '正文節奏好，適合長表單與研究問卷',
    sample: 'Long-form response text',
  },
  {
    family: 'Playfair Display',
    category: 'Display Serif',
    description: '高辨識標題字，適合精緻品牌開場',
    sample: 'Elegant opening screen',
  },
]

const fontPairs: FontPair[] = [
  {
    name: '產品清晰',
    heading: 'Geist',
    body: 'Noto Sans TC',
    description: '適合 SaaS、內部流程與正式問卷',
  },
  {
    name: '企業穩重',
    heading: 'Inter',
    body: 'Open Sans',
    description: '適合 B2B、CRM、營運資料收集',
  },
  {
    name: '繁中內容',
    heading: 'Noto Serif TC',
    body: 'Noto Sans TC',
    description: '適合教育、研究、內容型問卷',
  },
  {
    name: '活動品牌',
    heading: 'Montserrat',
    body: 'Source Sans 3',
    description: '適合活動報名與行銷問卷',
  },
  {
    name: '高端精緻',
    heading: 'Playfair Display',
    body: 'Lato',
    description: '適合品牌體驗與 VIP 回饋',
  },
]

const fontScaleOptions: Array<{
  value: FontScale
  label: string
  description: string
}> = [
  { value: 'compact', label: '緊湊', description: '較多內容，減少捲動' },
  { value: 'comfortable', label: '標準', description: '一般填答最平衡' },
  { value: 'large', label: '放大', description: '更醒目，適合簡短問卷' },
]

const layoutPresets: LayoutPreset[] = [
  {
    name: '經典左對齊',
    description: '最適合正式問卷與長問題，閱讀動線穩定。',
    icon: AlignLeft,
    settings: {
      questionAlign: 'left',
      verticalAlign: 'center',
      answerSpacing: 'comfortable',
      navigationLayout: 'inline',
      progressPosition: 'bottom',
      showQuestionNumber: true,
      showBackButton: true,
    },
    theme: { maxWidth: 720, radius: 8 },
  },
  {
    name: '聚焦置中',
    description: '適合短問卷、活動報名與更具引導感的流程。',
    icon: AlignCenter,
    settings: {
      questionAlign: 'center',
      verticalAlign: 'center',
      answerSpacing: 'roomy',
      navigationLayout: 'stacked',
      progressPosition: 'bottom',
      showQuestionNumber: true,
      showBackButton: true,
    },
    theme: { maxWidth: 640, radius: 12 },
  },
  {
    name: '緊湊效率',
    description: '減少留白與間距，適合題目較多的內部表單。',
    icon: Rows3,
    settings: {
      questionAlign: 'left',
      verticalAlign: 'top',
      answerSpacing: 'compact',
      navigationLayout: 'split',
      progressPosition: 'top',
      showQuestionNumber: true,
      showBackButton: true,
    },
    theme: { maxWidth: 820, radius: 6 },
  },
  {
    name: '極簡沉浸',
    description: '隱藏題號與返回，讓填答者專注單一路徑。',
    icon: SquareSplitHorizontal,
    settings: {
      questionAlign: 'center',
      verticalAlign: 'center',
      answerSpacing: 'comfortable',
      navigationLayout: 'stacked',
      progressPosition: 'bottom',
      showQuestionNumber: false,
      showBackButton: false,
    },
    theme: { maxWidth: 680, radius: 16 },
  },
]

const questionAlignOptions: Array<{
  value: FormQuestionAlignment
  label: string
  description: string
  icon: LucideIcon
}> = [
  { value: 'left', label: '左對齊', description: '長題目與說明最穩定', icon: AlignLeft },
  { value: 'center', label: '置中', description: '更像對話式引導', icon: AlignCenter },
  { value: 'right', label: '右對齊', description: '適合特殊品牌版面', icon: AlignRight },
]

const verticalAlignOptions: Array<{
  value: FormVerticalAlign
  label: string
  description: string
  icon: LucideIcon
}> = [
  { value: 'center', label: '置中顯示', description: '單題沉浸式填答', icon: MoveVertical },
  { value: 'top', label: '靠上顯示', description: '題目多或內容長時好掃描', icon: ArrowUpToLine },
]

const answerSpacingOptions: Array<{
  value: FormAnswerSpacing
  label: string
  description: string
  icon: LucideIcon
}> = [
  { value: 'compact', label: '緊湊', description: '縮短選項間距', icon: Rows2 },
  { value: 'comfortable', label: '標準', description: '一般填答節奏', icon: Rows3 },
  { value: 'roomy', label: '寬鬆', description: '大留白更沉浸', icon: Rows4 },
]

const navigationLayoutOptions: Array<{
  value: FormNavigationLayout
  label: string
  description: string
  icon: LucideIcon
}> = [
  { value: 'inline', label: '並排', description: '下一步、返回同列顯示', icon: Columns2 },
  { value: 'split', label: '左右分離', description: '返回靠左，下一步靠右', icon: StretchHorizontal },
  { value: 'stacked', label: '堆疊', description: '手機與置中版面更清楚', icon: ListCollapse },
]

const progressPositionOptions: Array<{
  value: FormProgressPosition
  label: string
  description: string
  icon: LucideIcon
}> = [
  { value: 'bottom', label: '底部', description: '不干擾題目閱讀', icon: PanelBottom },
  { value: 'top', label: '頂部', description: '一進入就看見進度', icon: PanelTop },
]

const brandPresets: BrandPreset[] = [
  {
    name: '正式品牌頁首',
    description: 'Logo 與品牌名固定在左上，底部保留低調標記。',
    settings: {
      showLogo: true,
      logoPosition: 'top-left',
      logoSize: 48,
      logoShape: 'rounded',
      logoFrame: true,
      showBrandName: true,
      showBrandMark: true,
      brandMarkPosition: 'bottom-center',
      brandMarkStyle: 'pill',
      brandMarkOpacity: 72,
    },
  },
  {
    name: '置中活動品牌',
    description: '品牌識別置中顯示，適合報名表與活動頁。',
    settings: {
      showLogo: true,
      logoPosition: 'top-center',
      logoSize: 56,
      logoShape: 'circle',
      logoFrame: true,
      showBrandName: true,
      showBrandMark: true,
      brandMarkPosition: 'bottom-center',
      brandMarkStyle: 'solid',
      brandMarkOpacity: 86,
    },
  },
  {
    name: '極簡無干擾',
    description: '隱藏頁首品牌，只在底部以文字標記來源。',
    settings: {
      showLogo: false,
      showBrandMark: true,
      brandMarkPosition: 'bottom-right',
      brandMarkStyle: 'subtle',
      brandMarkOpacity: 60,
    },
  },
  {
    name: '白標問卷',
    description: '隱藏頁首與底部標記，適合嵌入既有品牌流程。',
    settings: {
      showLogo: false,
      showBrandMark: false,
    },
  },
]

const logoPositionOptions: Array<{
  value: FormLogoPosition
  label: string
  description: string
}> = [
  { value: 'top-left', label: '左上', description: '正式、穩定，適合企業問卷' },
  { value: 'top-center', label: '置中', description: '品牌曝光最明顯' },
  { value: 'top-right', label: '右上', description: '保留左側閱讀起點' },
]

const logoShapeOptions: Array<{
  value: FormLogoShape
  label: string
  description: string
}> = [
  { value: 'rounded', label: '圓角', description: '產品與 SaaS 常用' },
  { value: 'circle', label: '圓形', description: '適合圖示型 Logo' },
  { value: 'none', label: '原始', description: '保留素材原本外觀' },
]

const brandMarkPositionOptions: Array<{
  value: FormBrandMarkPosition
  label: string
  description: string
}> = [
  { value: 'bottom-left', label: '左下', description: '低調但容易辨識' },
  { value: 'bottom-center', label: '置中', description: '最平均，不偏向閱讀側' },
  { value: 'bottom-right', label: '右下', description: '常見網站品牌標記位置' },
]

const brandMarkStyleOptions: Array<{
  value: FormBrandMarkStyle
  label: string
  description: string
}> = [
  { value: 'pill', label: '膠囊', description: '清楚、精緻，預設推薦' },
  { value: 'subtle', label: '文字', description: '最不干擾填答內容' },
  { value: 'solid', label: '實色', description: '需要明顯品牌露出時使用' },
]

const bannerDisplayOptions: Array<{
  value: FormBannerDisplay
  label: string
  description: string
}> = [
  { value: 'welcome', label: '歡迎頁', description: '作為問卷封面，最不干擾作答' },
  { value: 'questions', label: '題目頁', description: '每題上方保留品牌情境' },
  { value: 'all', label: '全流程', description: '歡迎、題目與完成頁都顯示' },
]

export function AppearancePanel() {
  const [bannerEditorOpen, setBannerEditorOpen] = useState(false)
  const form = useFormBuilderStore((state) => state.form)
  const updateForm = useFormBuilderStore((state) => state.updateForm)
  const appearancePreviewMode = useFormBuilderStore((state) => state.appearancePreviewMode)
  const setAppearancePreviewMode = useFormBuilderStore((state) => state.setAppearancePreviewMode)
  const theme = form.themeConfig
  const backgroundType = getBackgroundType(form.settings.backgroundType)
  const layout = getFormLayoutSettings(form.settings)
  const brand = getFormBrandSettings(form.settings)
  const showProgress = form.settings.showProgress !== false
  const fillMode = form.settings.fillMode === 'step' ? 'step' : 'one_page'
  const headingFontFamily = getFormHeadingFont(theme)
  const bodyFontFamily = getFormBodyFont(theme)
  const fontScale = theme.fontScale ?? 'comfortable'
  const lineHeight = theme.lineHeight ?? 1.55
  const bannerImageValue: EditableImageValue = {
    url: brand.bannerUrl,
    alt: brand.bannerAlt,
    fit: brand.bannerFit,
    focalX: brand.bannerFocalX,
    focalY: brand.bannerFocalY,
    zoom: brand.bannerZoom,
    radius: brand.bannerRadius,
    height: brand.bannerHeight,
  }

  function updateTheme(patch: Partial<FormTheme>) {
    updateForm({ themeConfig: { ...theme, ...patch } })
  }

  function updateSettings(patch: SettingsPatch) {
    updateForm({ settings: { ...form.settings, ...patch } })
  }

  function applyPreset(preset: ThemePreset) {
    updateForm({
      themeConfig: { ...preset.theme },
      settings: {
        ...form.settings,
        backgroundType: preset.backgroundType,
        showProgress,
      },
    })
  }

  function updateHeadingFont(headingFontFamily: string) {
    updateTheme({ headingFontFamily })
  }

  function updateBodyFont(bodyFontFamily: string) {
    updateTheme({ bodyFontFamily, fontFamily: bodyFontFamily })
  }

  function applyFontPair(pair: FontPair) {
    updateTheme({
      headingFontFamily: pair.heading,
      bodyFontFamily: pair.body,
      fontFamily: pair.body,
    })
  }

  function applyLayoutPreset(preset: LayoutPreset) {
    updateForm({
      themeConfig: preset.theme ? { ...theme, ...preset.theme } : theme,
      settings: { ...form.settings, ...preset.settings, showProgress },
    })
  }

  function applyBrandPreset(preset: BrandPreset) {
    updateSettings(preset.settings)
  }

  function updateBrandAsset(key: 'logoUrl' | 'faviconUrl', file: File | undefined) {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => {
      updateSettings({ [key]: String(reader.result ?? '') })
    }
    reader.readAsDataURL(file)
  }

  function updateBannerAsset(file: File | undefined) {
    if (!file) return
    const shouldSwitchToQuestionPreview = brand.bannerDisplay === 'welcome'
    const reader = new FileReader()
    reader.onload = () => {
      updateSettings({
        bannerUrl: String(reader.result ?? ''),
        bannerAlt: `${brand.brandName} 問卷 Banner`,
        bannerDisplay: shouldSwitchToQuestionPreview ? 'questions' : brand.bannerDisplay,
        showBanner: true,
      })
      setAppearancePreviewMode('brand')
      setBannerEditorOpen(true)
      if (shouldSwitchToQuestionPreview) {
        toast.info('已將 Banner 顯示位置切到題目頁，方便在主畫面立即預覽。')
      }
    }
    reader.readAsDataURL(file)
  }

  function updateBannerImage(value: EditableImageValue) {
    updateSettings({
      bannerUrl: value.url,
      bannerAlt: value.alt,
      bannerFit: value.fit,
      bannerFocalX: value.focalX,
      bannerFocalY: value.focalY,
      bannerZoom: value.zoom,
      bannerRadius: value.radius,
      bannerHeight: value.height ?? brand.bannerHeight,
      showBanner: Boolean(value.url),
    })
  }

  return (
    <Tabs
      value={appearancePreviewMode}
      onValueChange={(value) => setAppearancePreviewMode(value as AppearancePreviewMode)}
      className="flex min-h-0 flex-col"
    >
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="theme">主題</TabsTrigger>
        <TabsTrigger value="font">字體</TabsTrigger>
        <TabsTrigger value="layout">佈局</TabsTrigger>
        <TabsTrigger value="brand">品牌</TabsTrigger>
      </TabsList>

      <TabsContent value="theme" className="flex flex-col gap-4">
        <MainPreviewButton
          title="在主畫面顯示主題預覽"
          description="右側保留設定操作，主畫面會即時呈現填答頁的色彩、背景與按鈕風格。"
          isActive={appearancePreviewMode === 'theme'}
          onClick={() => setAppearancePreviewMode('theme')}
        />

        <section className="space-y-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">主題風格</h4>
            <p className="text-xs text-slate-500">選擇後會同步套用到填答頁面。</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {themePresets.map((preset) => {
              const isSelected =
                theme.backgroundColor === preset.theme.backgroundColor &&
                theme.accentColor === preset.theme.accentColor &&
                backgroundType === preset.backgroundType

              return (
                <button
                  key={preset.name}
                  type="button"
                  className={cn(
                    'rounded-md border border-slate-200 p-2 text-left transition hover:border-indigo-300 hover:bg-slate-50',
                    isSelected && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-100',
                  )}
                  onClick={() => applyPreset(preset)}
                >
                  <div
                    className="h-14 rounded"
                    style={{
                      background: getPreviewBackground(preset.theme, preset.backgroundType),
                    }}
                  />
                  <div className="mt-2 text-xs font-semibold text-slate-900">{preset.name}</div>
                  <div className="mt-0.5 line-clamp-2 text-[11px] text-slate-500">
                    {preset.description}
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        <section className="space-y-3 rounded-md border border-slate-200 p-3">
          <h4 className="text-sm font-semibold text-slate-950">自訂顏色</h4>
          <ColorInput
            label="背景色"
            value={theme.backgroundColor}
            onChange={(backgroundColor) => updateTheme({ backgroundColor })}
          />
          <ColorInput
            label="問題文字"
            value={theme.textColor}
            onChange={(textColor) => updateTheme({ textColor })}
          />
          <ColorInput
            label="答案文字"
            value={theme.answerColor}
            onChange={(answerColor) => updateTheme({ answerColor })}
          />
          <ColorInput
            label="按鈕色"
            value={theme.buttonColor}
            onChange={(buttonColor) => updateTheme({ buttonColor })}
          />
          <ColorInput
            label="按鈕文字"
            value={theme.buttonTextColor}
            onChange={(buttonTextColor) => updateTheme({ buttonTextColor })}
          />
          <ColorInput
            label="強調色"
            value={theme.accentColor}
            onChange={(accentColor) => updateTheme({ accentColor })}
          />
        </section>

        <section className="space-y-2">
          <Label>背景類型</Label>
          <Select
            value={backgroundType}
            onValueChange={(value) => updateSettings({ backgroundType: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="solid">純色</SelectItem>
              <SelectItem value="gradient">漸層</SelectItem>
              <SelectItem value="image">圖片</SelectItem>
              <SelectItem value="video">影片</SelectItem>
            </SelectContent>
          </Select>
          {(backgroundType === 'image' || backgroundType === 'video') && (
            <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600">
              媒體背景會在上傳品牌素材後套用，目前先以背景色與強調色產生可預覽的填答風格。
            </p>
          )}
        </section>
      </TabsContent>

      <TabsContent value="font" className="flex flex-col gap-4">
        <MainPreviewButton
          title="在主畫面顯示字體預覽"
          description="主畫面會放大顯示標題、描述、答案選項與按鈕文字的實際閱讀節奏。"
          isActive={appearancePreviewMode === 'font'}
          onClick={() => setAppearancePreviewMode('font')}
        />

        <section className="space-y-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">快速字體組合</h4>
            <p className="text-xs text-slate-500">一鍵設定標題與內文字體，適合快速建立風格。</p>
          </div>
          <div className="grid gap-2">
            {fontPairs.map((pair) => {
              const isSelected = headingFontFamily === pair.heading && bodyFontFamily === pair.body
              return (
                <button
                  key={pair.name}
                  type="button"
                  className={cn(
                    'rounded-md border border-slate-200 p-3 text-left transition hover:border-indigo-300 hover:bg-slate-50',
                    isSelected && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-100',
                  )}
                  onClick={() => applyFontPair(pair)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-950">{pair.name}</div>
                      <div className="mt-0.5 text-xs text-slate-500">{pair.description}</div>
                    </div>
                    <div className="shrink-0 text-right text-[11px] text-slate-500">
                      <div style={{ fontFamily: pair.heading }}>{pair.heading}</div>
                      <div style={{ fontFamily: pair.body }}>{pair.body}</div>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </section>

        <FontPicker
          label="標題字體"
          value={headingFontFamily}
          onChange={updateHeadingFont}
          helper="套用在歡迎頁、問題標題、完成頁標題。"
        />

        <FontPicker
          label="內文字體"
          value={bodyFontFamily}
          onChange={updateBodyFont}
          helper="套用在描述、答案選項、輸入框與導覽提示。"
        />

        <section className="space-y-3 rounded-md border border-slate-200 p-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">閱讀節奏</h4>
            <p className="text-xs text-slate-500">控制填答頁字級密度與行距。</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {fontScaleOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(
                  'rounded-md border border-slate-200 p-2 text-left transition hover:border-indigo-300 hover:bg-slate-50',
                  fontScale === option.value && 'border-indigo-500 bg-indigo-50 text-indigo-700',
                )}
                onClick={() => updateTheme({ fontScale: option.value })}
              >
                <div className="text-xs font-semibold">{option.label}</div>
                <div className="mt-1 text-[11px] leading-snug text-slate-500">
                  {option.description}
                </div>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <Label>行距</Label>
              <span className="text-xs tabular-nums text-slate-500">{lineHeight.toFixed(2)}</span>
            </div>
            <Slider
              value={[lineHeight]}
              min={1.25}
              max={1.8}
              step={0.05}
              onValueChange={([nextLineHeight]) => updateTheme({ lineHeight: nextLineHeight })}
            />
          </div>
        </section>
      </TabsContent>

      <TabsContent value="layout" className="flex flex-col gap-5">
        <section className="space-y-2">
          <h4 className="text-sm font-semibold text-slate-950">快速佈局</h4>
          <div className="grid grid-cols-4 gap-2">
            {layoutPresets.map((preset) => {
              const Icon = preset.icon
              const isSelected =
                layout.questionAlign === preset.settings.questionAlign &&
                layout.verticalAlign === preset.settings.verticalAlign &&
                layout.answerSpacing === preset.settings.answerSpacing &&
                layout.navigationLayout === preset.settings.navigationLayout &&
                layout.progressPosition === preset.settings.progressPosition &&
                layout.showQuestionNumber === preset.settings.showQuestionNumber &&
                layout.showBackButton === preset.settings.showBackButton

              return (
                <button
                  key={preset.name}
                  type="button"
                  title={preset.name}
                  aria-label={preset.name}
                  className={cn(
                    'grid h-12 place-items-center rounded-md border border-slate-200 text-slate-600 transition hover:border-indigo-300 hover:bg-slate-50 hover:text-indigo-700',
                    isSelected && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-100',
                  )}
                  onClick={() => applyLayoutPreset(preset)}
                >
                  <Icon className="size-5" />
                  <span className="sr-only">{preset.name}</span>
                </button>
              )
            })}
          </div>
        </section>

        <OptionGrid
          title="問題對齊"
          options={questionAlignOptions}
          value={layout.questionAlign}
          onChange={(questionAlign) => updateSettings({ questionAlign })}
          iconOnly
        />

        <OptionGrid
          title="垂直位置"
          options={verticalAlignOptions}
          value={layout.verticalAlign}
          onChange={(verticalAlign) => updateSettings({ verticalAlign })}
          iconOnly
        />

        <OptionGrid
          title="答案間距"
          options={answerSpacingOptions}
          value={layout.answerSpacing}
          onChange={(answerSpacing) => updateSettings({ answerSpacing })}
          iconOnly
        />

        <OptionGrid
          title="導覽按鈕"
          options={navigationLayoutOptions}
          value={layout.navigationLayout}
          onChange={(navigationLayout) => updateSettings({ navigationLayout })}
          iconOnly
        />

        <OptionGrid
          title="進度位置"
          options={progressPositionOptions}
          value={layout.progressPosition}
          onChange={(progressPosition) => updateSettings({ progressPosition })}
          iconOnly
        />

        <section className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label>填答內容最大寬度</Label>
            <span className="text-xs tabular-nums text-slate-500">{theme.maxWidth}px</span>
          </div>
          <Slider
            value={[theme.maxWidth]}
            min={400}
            max={900}
            step={20}
            onValueChange={([maxWidth]) => updateTheme({ maxWidth })}
          />
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <Label>元件圓角</Label>
            <span className="text-xs tabular-nums text-slate-500">{theme.radius}px</span>
          </div>
          <Slider
            value={[theme.radius]}
            min={0}
            max={24}
            step={1}
            onValueChange={([radius]) => updateTheme({ radius })}
          />
        </section>

        <section className="space-y-2">
          <Label>進度條樣式</Label>
          <Select
            value={theme.progressStyle}
            onValueChange={(progressStyle: FormTheme['progressStyle']) =>
              updateTheme({ progressStyle })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="thin">細線</SelectItem>
              <SelectItem value="thick">粗線</SelectItem>
              <SelectItem value="text">只顯示百分比</SelectItem>
            </SelectContent>
          </Select>
        </section>

        <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm transition hover:border-indigo-200 hover:bg-indigo-50/40">
          <div>
            <div className="font-medium text-slate-900">顯示進度條</div>
          </div>
          <Switch
            aria-label="顯示進度條"
            checked={showProgress}
            onCheckedChange={(checked) => updateSettings({ showProgress: checked })}
          />
        </div>

        <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm transition hover:border-indigo-200 hover:bg-indigo-50/40">
          <div>
            <div className="font-medium text-slate-900">顯示題號</div>
          </div>
          <Switch
            aria-label="顯示題號"
            checked={layout.showQuestionNumber}
            onCheckedChange={(showQuestionNumber) => updateSettings({ showQuestionNumber })}
          />
        </div>

        {fillMode === 'step' ? (
          <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm transition hover:border-indigo-200 hover:bg-indigo-50/40">
            <div>
              <div className="font-medium text-slate-900">顯示返回按鈕</div>
            </div>
            <Switch
              aria-label="顯示返回按鈕"
              checked={layout.showBackButton}
              onCheckedChange={(showBackButton) => updateSettings({ showBackButton })}
            />
          </div>
        ) : null}
      </TabsContent>

      <TabsContent value="brand" className="flex flex-col gap-5">
        <MainPreviewButton
          title="在主畫面顯示品牌預覽"
          description="Logo、Banner 與底部品牌標記會在中央填答畫面中同步顯示。"
          isActive={appearancePreviewMode === 'brand'}
          onClick={() => setAppearancePreviewMode('brand')}
        />

        <BannerAssetPanel
          brand={brand}
          theme={theme}
          backgroundType={backgroundType}
          onFileChange={updateBannerAsset}
          onOpenEditor={() => setBannerEditorOpen(true)}
          onSettingsChange={updateSettings}
        />

        <ImageEditorDialog
          open={bannerEditorOpen}
          onOpenChange={setBannerEditorOpen}
          title="編輯問卷 Banner 圖片"
          description="拖曳預覽圖可移動裁切位置，也可調整縮放、高度、圓角與顯示位置。這裡只使用前端預覽，不接 R2。"
          value={bannerImageValue}
          defaultAlt={`${brand.brandName} 問卷 Banner`}
          uploadId="brand-banner-editor-upload"
          previewBackground={getPreviewBackground(theme, backgroundType)}
          showHeightControl
          heightRange={{ min: 120, max: 520, step: 10 }}
          onChange={updateBannerImage}
          onRemove={() => updateSettings({ bannerUrl: '', showBanner: false })}
          extraControls={
            <div className="space-y-5">
              <OptionGrid
                title="顯示位置"
                options={bannerDisplayOptions}
                value={brand.bannerDisplay}
                onChange={(bannerDisplay) => updateSettings({ bannerDisplay })}
              />
              <div className="space-y-2">
                <Label>遮罩顏色</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={brand.bannerOverlayColor}
                    className="h-9 w-12 shrink-0 p-1"
                    onChange={(event) => updateSettings({ bannerOverlayColor: event.target.value })}
                  />
                  <Input
                    value={brand.bannerOverlayColor}
                    className="h-9 min-w-0 font-mono text-xs"
                    onChange={(event) => updateSettings({ bannerOverlayColor: event.target.value })}
                  />
                </div>
              </div>
              <SliderControl
                label="遮罩透明度"
                value={brand.bannerOverlayOpacity}
                min={0}
                max={80}
                step={1}
                suffix="%"
                onChange={(bannerOverlayOpacity) => updateSettings({ bannerOverlayOpacity })}
              />
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
                <div>
                  <div className="font-medium text-slate-900">顯示 Banner</div>
                  <div className="text-xs text-slate-500">關閉後保留圖片與裁切設定。</div>
                </div>
                <Switch
                  aria-label="顯示 Banner"
                  checked={brand.showBanner}
                  onCheckedChange={(showBanner) => updateSettings({ showBanner })}
                />
              </div>
            </div>
          }
        />

        <section className="space-y-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-950">快速品牌配置</h4>
            <p className="text-xs text-slate-500">
              設定問卷填答頁的品牌露出方式，可用於活動、客戶回饋或嵌入網站。
            </p>
          </div>
          <div className="grid gap-2">
            {brandPresets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                className="rounded-md border border-slate-200 p-3 text-left transition hover:border-indigo-300 hover:bg-slate-50"
                onClick={() => applyBrandPreset(preset)}
              >
                <div className="text-sm font-semibold text-slate-950">{preset.name}</div>
                <div className="mt-1 text-xs leading-relaxed text-slate-500">
                  {preset.description}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-3 rounded-md border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-950">頁首品牌</h4>
              <p className="text-xs text-slate-500">顯示在填答頁左上、置中或右上。</p>
            </div>
            <Switch
              aria-label="顯示頁首品牌"
              checked={brand.showLogo}
              onCheckedChange={(showLogo) => updateSettings({ showLogo })}
            />
          </div>

          <div className="space-y-2">
            <Label>品牌名稱</Label>
            <Input
              value={brand.brandName}
              placeholder="例如：Miao Form"
              onChange={(event) =>
                updateSettings({
                  brandName: event.target.value,
                  logoAlt: event.target.value,
                })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-2">
              <Label>Logo 文字</Label>
              <Input
                value={brand.logoText}
                maxLength={4}
                placeholder="MF"
                onChange={(event) => updateSettings({ logoText: event.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>替代文字</Label>
              <Input
                value={brand.logoAlt}
                placeholder="Logo 說明"
                onChange={(event) => updateSettings({ logoAlt: event.target.value })}
              />
            </div>
          </div>

          <AssetPicker
            label="Logo 圖片"
            value={brand.logoUrl}
            accept="image/*"
            helper="可上傳 PNG / JPG / SVG，或貼上已公開的圖片 URL。"
            onFileChange={(file) => updateBrandAsset('logoUrl', file)}
            onUrlChange={(logoUrl) => updateSettings({ logoUrl })}
            onClear={() => updateSettings({ logoUrl: '' })}
          />

          <div className="space-y-2">
            <Label>Logo 點擊導向網址</Label>
            <div className="relative">
              <Link className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
              <Input
                value={brand.logoLinkUrl}
                className="pl-9"
                placeholder="https://example.com"
                onChange={(event) => updateSettings({ logoLinkUrl: event.target.value })}
              />
            </div>
          </div>

          <OptionGrid
            title="Logo 位置"
            options={logoPositionOptions}
            value={brand.logoPosition}
            onChange={(logoPosition) => updateSettings({ logoPosition })}
          />

          <OptionGrid
            title="Logo 外型"
            options={logoShapeOptions}
            value={brand.logoShape}
            onChange={(logoShape) => updateSettings({ logoShape })}
          />

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <Label>Logo 大小</Label>
              <span className="text-xs tabular-nums text-slate-500">{brand.logoSize}px</span>
            </div>
            <Slider
              value={[brand.logoSize]}
              min={32}
              max={160}
              step={4}
              onValueChange={([logoSize]) => updateSettings({ logoSize })}
            />
          </div>

          <div className="grid gap-2">
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
              <div>
                <div className="font-medium text-slate-900">顯示品牌名稱</div>
                <div className="text-xs text-slate-500">Logo 旁同步顯示文字品牌。</div>
              </div>
              <Switch
                aria-label="顯示品牌名稱"
                checked={brand.showBrandName}
                onCheckedChange={(showBrandName) => updateSettings({ showBrandName })}
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3 text-sm">
              <div>
                <div className="font-medium text-slate-900">Logo 外框</div>
                <div className="text-xs text-slate-500">在複雜背景上提升辨識度。</div>
              </div>
              <Switch
                aria-label="Logo 外框"
                checked={brand.logoFrame}
                onCheckedChange={(logoFrame) => updateSettings({ logoFrame })}
              />
            </div>
          </div>
        </section>

        <section className="space-y-3 rounded-md border border-slate-200 p-3">
          <h4 className="text-sm font-semibold text-slate-950">Favicon</h4>
          <p className="text-xs text-slate-500">
            設定瀏覽器分頁小圖示，分享連結或多分頁操作時更容易辨識。
          </p>
          <AssetPicker
            label="分頁圖示"
            value={brand.faviconUrl}
            accept="image/*"
            helper="建議使用正方形 PNG / ICO / SVG。"
            onFileChange={(file) => updateBrandAsset('faviconUrl', file)}
            onUrlChange={(faviconUrl) => updateSettings({ faviconUrl })}
            onClear={() => updateSettings({ faviconUrl: '' })}
          />
        </section>

        <section className="space-y-3 rounded-md border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-950">底部品牌標記</h4>
              <p className="text-xs text-slate-500">可顯示產品來源、活動單位或自訂品牌連結。</p>
            </div>
            <Switch
              aria-label="顯示底部品牌標記"
              checked={brand.showBrandMark}
              onCheckedChange={(showBrandMark) => updateSettings({ showBrandMark })}
            />
          </div>

          <div className="space-y-2">
            <Label>標記文字</Label>
            <Input
              value={brand.brandMarkText}
              placeholder="Powered by Miao Form"
              onChange={(event) => updateSettings({ brandMarkText: event.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>標記連結</Label>
            <div className="relative">
              <Link className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
              <Input
                value={brand.brandMarkUrl}
                className="pl-9"
                placeholder="https://example.com"
                onChange={(event) => updateSettings({ brandMarkUrl: event.target.value })}
              />
            </div>
          </div>

          <OptionGrid
            title="標記位置"
            options={brandMarkPositionOptions}
            value={brand.brandMarkPosition}
            onChange={(brandMarkPosition) => updateSettings({ brandMarkPosition })}
          />

          <OptionGrid
            title="標記樣式"
            options={brandMarkStyleOptions}
            value={brand.brandMarkStyle}
            onChange={(brandMarkStyle) => updateSettings({ brandMarkStyle })}
          />

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <Label>標記透明度</Label>
              <span className="text-xs tabular-nums text-slate-500">{brand.brandMarkOpacity}%</span>
            </div>
            <Slider
              value={[brand.brandMarkOpacity]}
              min={40}
              max={100}
              step={4}
              onValueChange={([brandMarkOpacity]) => updateSettings({ brandMarkOpacity })}
            />
          </div>
        </section>
      </TabsContent>
    </Tabs>
  )
}

function MainPreviewButton({
  title,
  description,
  isActive,
  onClick,
}: {
  title: string
  description: string
  isActive: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      className={cn(
        'flex w-full items-start gap-3 rounded-md border p-3 text-left transition',
        isActive
          ? 'border-indigo-500 bg-indigo-50 text-indigo-800 ring-1 ring-indigo-100'
          : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50',
      )}
      onClick={onClick}
    >
      <span
        className={cn(
          'mt-0.5 grid size-8 shrink-0 place-items-center rounded-md border',
          isActive
            ? 'border-indigo-200 bg-white text-indigo-600'
            : 'border-slate-200 bg-slate-50 text-slate-500',
        )}
      >
        <Eye className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span
          className={cn(
            'mt-1 block text-xs leading-relaxed',
            isActive ? 'text-indigo-700' : 'text-slate-500',
          )}
        >
          {description}
        </span>
      </span>
    </button>
  )
}

function BannerAssetPanel({
  brand,
  theme,
  backgroundType,
  onFileChange,
  onOpenEditor,
  onSettingsChange,
}: {
  brand: FormBrandSettings
  theme: FormTheme
  backgroundType: BackgroundType
  onFileChange: (file: File | undefined) => void
  onOpenEditor: () => void
  onSettingsChange: (patch: SettingsPatch) => void
}) {
  return (
    <section className="space-y-3 rounded-md border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">問卷 Banner 圖片</h4>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            上傳後會立即開啟圖片編輯器，可調整裁切焦點、縮放、高度、圓角與遮罩。
          </p>
        </div>
        <Switch
          aria-label="顯示問卷 Banner"
          checked={brand.showBanner}
          disabled={!brand.bannerUrl}
          onCheckedChange={(showBanner) => onSettingsChange({ showBanner })}
        />
      </div>

      <label
        htmlFor="brand-banner-upload"
        className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed border-slate-300 p-3 text-sm transition hover:border-indigo-300 hover:bg-slate-50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <Upload className="size-4 text-slate-500" />
          <span className="truncate text-slate-700">
            {brand.bannerUrl ? '重新上傳 Banner' : '上傳 Banner 圖片'}
          </span>
        </span>
        <span className="text-xs text-slate-500">選擇檔案後編輯</span>
        <Input
          id="brand-banner-upload"
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(event) => onFileChange(event.target.files?.[0])}
        />
      </label>

      {brand.bannerUrl && brand.showBanner && brand.bannerDisplay === 'welcome' && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
          <div className="font-medium">目前 Banner 顯示在歡迎頁</div>
          <p className="mt-1">
            主畫面現在是題目頁預覽，所以不會看到這張 Banner。請切換預覽或改顯示位置。
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            onClick={() => onSettingsChange({ bannerDisplay: 'questions' })}
          >
            改顯示在題目頁
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <Label>Banner 圖片 URL</Label>
        <div className="relative">
          <ImageIcon className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
          <Input
            value={brand.bannerUrl}
            className="pr-10 pl-9"
            placeholder="https://example.com/banner.jpg"
            onChange={(event) =>
              onSettingsChange({
                bannerUrl: event.target.value,
                showBanner: Boolean(event.target.value),
              })
            }
            onBlur={() => {
              if (brand.bannerUrl) onOpenEditor()
            }}
          />
          {brand.bannerUrl && (
            <button
              type="button"
              className="-translate-y-1/2 absolute top-1/2 right-2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="清除問卷 Banner"
              onClick={() => onSettingsChange({ bannerUrl: '', showBanner: false })}
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>

      {brand.bannerUrl ? (
        <div className="space-y-3">
          <BannerPreviewFrame brand={brand} theme={theme} backgroundType={backgroundType} compact />
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" onClick={onOpenEditor}>
              <Pencil className="mr-2 size-4" />
              編輯圖片
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onSettingsChange({ bannerUrl: '', showBanner: false })}
            >
              <X className="mr-2 size-4" />
              移除 Banner
            </Button>
          </div>
        </div>
      ) : (
        <div className="rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
          建議使用 16:9 或 3:1 橫幅圖。正式上線時可改接 R2，上傳流程目前維持前端 mock-safe。
        </div>
      )}
    </section>
  )
}

function BannerPreviewFrame({
  brand,
  theme,
  backgroundType,
  compact = false,
}: {
  brand: FormBrandSettings
  theme: FormTheme
  backgroundType: BackgroundType
  compact?: boolean
}) {
  const height = compact ? Math.min(brand.bannerHeight, 160) : brand.bannerHeight

  return (
    <div
      className="overflow-hidden border border-slate-200 bg-white shadow-sm"
      style={{ borderRadius: brand.bannerRadius }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          background: getPreviewBackground(theme, backgroundType),
          borderRadius: brand.bannerRadius,
          height,
        }}
      >
        <img
          src={brand.bannerUrl}
          alt={brand.bannerAlt}
          className="size-full"
          style={{
            objectFit: brand.bannerFit,
            objectPosition: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
            transform: `scale(${brand.bannerZoom})`,
            transformOrigin: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
          }}
        />
        {brand.bannerOverlayOpacity > 0 && (
          <div
            className="absolute inset-0"
            style={{
              background: brand.bannerOverlayColor,
              opacity: brand.bannerOverlayOpacity / 100,
            }}
          />
        )}
        {!compact && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="rounded-full border border-white/30 bg-white/75 px-3 py-1.5 text-xs font-medium text-slate-700 backdrop-blur">
              預覽裁切焦點與遮罩
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function SliderControl({
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix: string
  onChange: (value: number) => void
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <Label>{label}</Label>
        <span className="text-xs tabular-nums text-slate-500">
          {Number.isInteger(value) ? value : value.toFixed(2)}
          {suffix}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([nextValue]) => onChange(nextValue)}
      />
    </div>
  )
}

function AssetPicker({
  label,
  value,
  accept,
  helper,
  onFileChange,
  onUrlChange,
  onClear,
}: {
  label: string
  value: string
  accept: string
  helper: string
  onFileChange: (file: File | undefined) => void
  onUrlChange: (value: string) => void
  onClear: () => void
}) {
  const fileInputId = `asset-${label}`

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="grid gap-2">
        <label
          htmlFor={fileInputId}
          className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed border-slate-300 p-3 text-sm transition hover:border-indigo-300 hover:bg-slate-50"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Upload className="size-4 text-slate-500" />
            <span className="truncate text-slate-700">上傳素材</span>
          </span>
          <span className="text-xs text-slate-500">選擇檔案</span>
          <Input
            id={fileInputId}
            type="file"
            accept={accept}
            className="sr-only"
            onChange={(event) => onFileChange(event.target.files?.[0])}
          />
        </label>

        <div className="relative">
          <ImageIcon className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-slate-400" />
          <Input
            value={value}
            className="pr-10 pl-9"
            placeholder="https://example.com/logo.png"
            onChange={(event) => onUrlChange(event.target.value)}
          />
          {value && (
            <button
              type="button"
              className="-translate-y-1/2 absolute top-1/2 right-2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label={`清除${label}`}
              onClick={onClear}
            >
              <X className="size-4" />
            </button>
          )}
        </div>
      </div>
      <p className="text-xs leading-relaxed text-slate-500">{helper}</p>
      {value && (
        <div className="flex items-center gap-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">
          <ImageIcon className="size-4 shrink-0 text-slate-400" />
          <span className="min-w-0 flex-1 truncate">{value}</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-slate-600 transition hover:border-indigo-300 hover:text-indigo-700"
            onClick={onClear}
          >
            <RotateCcw className="size-3.5" />
            清除
          </button>
        </div>
      )}
    </div>
  )
}

function OptionGrid<T extends string>({
  title,
  options,
  value,
  onChange,
  iconOnly = false,
}: {
  title: string
  options: Array<{ value: T; label: string; description: string; icon?: LucideIcon }>
  value: T
  onChange: (value: T) => void
  iconOnly?: boolean
}) {
  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-950">{title}</h4>
      <div className={cn('grid gap-2', iconOnly ? 'grid-cols-3' : '')}>
        {options.map((option) => {
          const Icon = option.icon ?? SquareSplitHorizontal
          return (
            <button
              key={option.value}
              type="button"
              title={option.label}
              aria-label={option.label}
              className={cn(
                iconOnly
                  ? 'grid h-12 place-items-center rounded-md border border-slate-200 text-slate-600 transition hover:border-indigo-300 hover:bg-slate-50 hover:text-indigo-700'
                  : 'rounded-md border border-slate-200 p-3 text-left transition hover:border-indigo-300 hover:bg-slate-50',
                value === option.value && 'border-indigo-500 bg-indigo-50 text-indigo-700',
              )}
              onClick={() => onChange(option.value)}
            >
              {iconOnly ? (
                <>
                  <Icon className="size-5" />
                  <span className="sr-only">{option.label}</span>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold">{option.label}</div>
                  <div className="mt-1 text-xs leading-relaxed text-slate-500">
                    {option.description}
                  </div>
                </>
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}

function FontPicker({
  label,
  value,
  helper,
  onChange,
}: {
  label: string
  value: string
  helper: string
  onChange: (value: string) => void
}) {
  return (
    <section className="space-y-3 rounded-md border border-slate-200 p-3">
      <div className="space-y-2">
        <div>
          <Label>{label}</Label>
          <p className="mt-1 text-xs text-slate-500">{helper}</p>
        </div>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fontOptions.map((font) => (
              <SelectItem key={font.family} value={font.family}>
                {font.family}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {fontOptions.map((font) => {
          const isSelected = value === font.family
          return (
            <button
              key={font.family}
              type="button"
              className={cn(
                'min-h-28 rounded-md border border-slate-200 p-3 text-left transition hover:border-indigo-300 hover:bg-slate-50',
                isSelected && 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-100',
              )}
              onClick={() => onChange(font.family)}
            >
              <div className="text-[11px] font-medium text-slate-500">{font.category}</div>
              <div
                className="mt-2 text-lg font-semibold leading-tight text-slate-950"
                style={{ fontFamily: font.family }}
              >
                {font.sample}
              </div>
              <div className="mt-2 text-xs leading-snug text-slate-500">{font.description}</div>
              <div className="mt-2 font-mono text-[11px] text-slate-400">{font.family}</div>
            </button>
          )
        })}
      </div>
    </section>
  )
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="grid grid-cols-[80px_1fr] items-center gap-2">
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="flex min-w-0 gap-2">
        <Input
          type="color"
          value={value}
          className="h-9 w-12 shrink-0 p-1"
          onChange={(event) => onChange(event.target.value)}
        />
        <Input
          value={value}
          className="h-9 min-w-0 font-mono text-xs"
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    </div>
  )
}

function getBackgroundType(value: unknown): BackgroundType {
  if (value === 'gradient' || value === 'image' || value === 'video') return value
  return 'solid'
}

function getPreviewBackground(theme: FormTheme, backgroundType: BackgroundType) {
  if (backgroundType === 'gradient') {
    return `linear-gradient(135deg, ${theme.backgroundColor} 0%, ${theme.accentColor} 100%)`
  }

  if (backgroundType === 'image' || backgroundType === 'video') {
    return `radial-gradient(circle at 20% 15%, ${theme.accentColor}33, transparent 34%), ${theme.backgroundColor}`
  }

  return theme.backgroundColor
}
